import type { Risposta, Sezione } from '../api/generated/dob';

export interface MifidAnswer {
  descRisposta: string;
  idRisposta: number;
}

// Risposte salvate di una domanda "padre" (es. 285) come lista di valori univoci.
// Splitta anche i descRisposta con la virgola, così le righe salvate in passato
// come "331,330,331" vengono ricondotte ai singoli id.
export const getUniqueAnswerValues = (risposte: Risposta[] = []): string[] => {
  const values = risposte.flatMap((r) => {
    const raw = r.descRisposta ? r.descRisposta : r.idRisposta?.toString() ?? '';
    return raw
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v !== '' && v !== '0');
  });
  return Array.from(new Set(values));
};

// Id delle risposte principali (senza dipendenze) della domanda contenitore,
// es. 284 -> {329, 330}. Le altre (331, 332, 333) sono le sotto-risposte.
export const getMainAnswerIds = (
  sezioni: Sezione[] | undefined,
  idDomandaContenitore: number | undefined
): Set<string> | undefined => {
  const contenitore = sezioni
    ?.flatMap((s) => s.listaDomande ?? [])
    .find((d) => d.idDomanda === idDomandaContenitore);
  const risposte = contenitore?.strutturaInternaDomande?.listaDominiRisposte?.at(0)?.risposte;
  const mainIds = risposte
    ?.filter((r) => !r.listaDipendenze?.length)
    .map((r) => r.idRisposta?.toString() ?? '');
  return mainIds?.length ? new Set(mainIds) : undefined;
};

// Divide le risposte tra domanda principale e sotto-domanda.
// Senza informazioni dal template mantiene il comportamento storico: la prima
// risposta è la principale, le altre sono le sotto-risposte.
export const splitMainAndSubAnswers = (
  values: string[],
  mainIds: Set<string> | undefined
): { main: string[]; sub: string[] } => {
  if (!mainIds) {
    return { main: values.slice(0, 1), sub: values.slice(1) };
  }
  return {
    main: values.filter((v) => mainIds.has(v)),
    sub: values.filter((v) => !mainIds.has(v)),
  };
};

export const parseMultipleAnswer = (value: string): MifidAnswer[] =>
  Array.from(
    new Set(
      value
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v !== '')
    )
  ).map((v) => ({ descRisposta: v, idRisposta: Number(v) }));

export const mergeAnswers = <T extends { descRisposta?: string }>(
  current: T[] = [],
  added: T[] = []
): T[] =>
  current.concat(
    added.filter((a) => !current.some((c) => c.descRisposta === a.descRisposta))
  );
